/*
 * Insights — a dedicated full-screen page for the spending graphs, opened from
 * the dashboard nav. Loads the current (cycle-aware) month's lines and renders
 * the SpendCharts panel with room to breathe, instead of crowding the dashboard.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useState } from 'react';
import AppNav from '../components/AppNav.jsx';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import SpendCharts from '../components/SpendCharts.jsx';
import { fmtR, spentOf } from '../money.js';

// Four-card summary strip (Pass 7) — the question you arrived with: what did I
// spend, on what, where do I keep going back, and am I up or down on last month.
// Every figure is derived from the same month lines and visit log the charts use.
function SummaryStrip({ items, visits, months, month }) {
  const expenses = (items || []).filter((i) => i.kind === 'expense' && i.source !== 'transfer');
  const spent = expenses.reduce((a, e) => a + spentOf(e), 0);
  const byCat = {};
  expenses.forEach((e) => { const k = e.category_name || 'Uncategorised'; if (spentOf(e) > 0) byCat[k] = (byCat[k] || 0) + spentOf(e); });
  const topCat = Object.entries(byCat).sort((a, b) => b[1] - a[1])[0];
  // Most visited — confirmed visit lines grouped by place (a visit counts even at R0).
  const byPlace = {};
  (visits || []).forEach((v) => { if (v.place_name) byPlace[v.place_name] = (byPlace[v.place_name] || 0) + 1; });
  const topPlace = Object.entries(byPlace).sort((a, b) => b[1] - a[1])[0];
  // Vs last month — this month's spend against the previous month in the list.
  const idx = (months || []).findIndex((m) => m.month === month);
  const prev = idx > 0 ? months[idx - 1] : null;
  const delta = prev ? spent - (Number(prev.total_expense) || 0) : null;
  const cards = [
    ['Spent this month', fmtR(spent), `${expenses.filter((e) => spentOf(e) > 0).length} item${expenses.filter((e) => spentOf(e) > 0).length === 1 ? '' : 's'}`, ''],
    ['Biggest category', topCat ? topCat[0] : '—', topCat ? fmtR(topCat[1]) : 'nothing spent yet', ''],
    ['Most visited', topPlace ? topPlace[0] : '—', topPlace ? `${topPlace[1]} visit${topPlace[1] === 1 ? '' : 's'}` : 'no visits logged', ''],
    delta === null
      ? ['Vs last month', '—', 'no prior month yet', '']
      : ['Vs last month', `${delta <= 0 ? '−' : '+'}${fmtR(Math.abs(delta))}`, delta <= 0 ? 'you spent less' : 'you spent more', delta <= 0 ? 'text-emerald-400' : 'text-red-400'],
  ];
  return (
    <div className="grid gap-2 mb-5" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))' }}>
      {cards.map(([k, v, sub, cls]) => (
        <div key={k} className="rounded-xl border border-edge bg-white/[0.03] px-4 py-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
          <div className={`text-lg font-bold truncate ${cls || 'text-slate-100'}`}>{v}</div>
          <div className="text-[10px] text-slate-500 truncate">{sub}</div>
        </div>
      ))}
    </div>
  );
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const shortMonth = (m) => { const q = String(m || '').split('-'); return q.length === 2 ? (MON[Number(q[1]) - 1] || m) : m; };

// Month-by-month spent vs leftover — the trend above the per-category bars.
function MonthTrend({ months }) {
  if (!months || months.length < 2) return null;
  const max = Math.max(1, ...months.flatMap((m) => [Number(m.total_expense) || 0, Math.max(0, Number(m.leftover) || 0)]));
  return (
    <div className="card mb-5">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-bold">Month by month</h2>
        <span className="text-[11px] text-slate-500"><span className="text-red-400">spent</span> vs <span className="text-emerald-400">leftover</span></span>
      </div>
      <div className="flex items-end gap-3" style={{ height: '9rem' }}>
        {months.map((m) => {
          const sp = Number(m.total_expense) || 0;
          const lo = Math.max(0, Number(m.leftover) || 0);
          return (
            <div key={m.month} className="flex-1 min-w-0 flex flex-col items-center gap-1">
              <div className="flex items-end justify-center gap-1 w-full" style={{ height: '7.5rem' }}>
                <div className="w-3 rounded-t bg-red-400/80" style={{ height: `${(sp / max) * 100}%` }} title={`${shortMonth(m.month)} spent ${fmtR(sp)}`} />
                <div className="w-3 rounded-t bg-emerald-400/80" style={{ height: `${(lo / max) * 100}%` }} title={`${shortMonth(m.month)} leftover ${fmtR(lo)}`} />
              </div>
              <span className="text-[9px] text-slate-500 truncate w-full text-center">{shortMonth(m.month)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// "What Vault noticed" — plain-data observations for THIS month (no AI, no
// invented numbers): the biggest spend, anything over plan, money left to move
// to savings, and where it went. Each reuses spentOf() so the rands match the
// charts and the dashboard exactly.
function Noticed({ items, months, month }) {
  const expenses = (items || []).filter((i) => i.kind === 'expense' && i.source !== 'transfer');
  const spent = expenses.filter((i) => spentOf(i) > 0);
  const tips = [];

  if (spent.length) {
    const top = spent.slice().sort((a, b) => spentOf(b) - spentOf(a))[0];
    tips.push({
      key: 'biggest', t: 'Your biggest spend', tag: 'trim one',
      tagCls: 'text-vpurple border-vpurple/40',
      b: `${top.title} took ${fmtR(spentOf(top))} this month${top.place_name ? ` at ${top.place_name}` : ''} — the first place to look if you want to trim.`,
      cta: top.place_name ? { label: 'Open in the map', to: '/app?open=map' } : null,
    });
  }

  const over = expenses
    .filter((i) => Number(i.amount) > 0 && spentOf(i) > Number(i.amount))
    .sort((a, b) => (spentOf(b) - Number(b.amount)) - (spentOf(a) - Number(a.amount)))[0];
  if (over) {
    const gap = spentOf(over) - Number(over.amount);
    tips.push({
      key: 'over', t: 'One went over plan', tag: 'over',
      tagCls: 'text-amber-400 border-amber-400/40',
      b: `${over.title} is ${fmtR(gap)} over its ${fmtR(over.amount)} plan (${fmtR(spentOf(over))} spent).`,
      cta: null,
    });
  }

  const cur = (months || []).find((m) => m.month === month);
  const left = cur ? Number(cur.leftover) : NaN;
  if (Number.isFinite(left) && left > 0) {
    tips.push({
      key: 'save', t: 'You have money left', tag: 'move it to savings',
      tagCls: 'text-emerald-400 border-emerald-400/40',
      b: `${fmtR(left)} is unspent this month — moving some into savings now keeps it out of everyday spending.`,
      cta: { label: 'Move to savings', to: '/manage' },
    });
  }

  const byShop = {};
  spent.forEach((e) => { if (e.place_name) byShop[e.place_name] = (byShop[e.place_name] || 0) + spentOf(e); });
  const shops = Object.entries(byShop).sort((a, b) => b[1] - a[1]);
  if (shops.length) {
    tips.push({
      key: 'shop', t: 'Where it went', tag: `${shops.length} shop${shops.length === 1 ? '' : 's'}`,
      tagCls: 'text-vblue border-vblue/40',
      b: `Most went to ${shops[0][0]} (${fmtR(shops[0][1])})${shops.length > 1 ? `, across ${shops.length} places in all.` : '.'}`,
      cta: { label: 'Open in the map', to: '/app?open=map' },
    });
  }

  if (!tips.length) return null;
  return (
    <div className="mb-5">
      <h2 className="text-[10px] uppercase tracking-[0.14em] text-vpurple mb-2">✨ What Vault noticed</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {tips.map((n) => (
          <div key={n.key} className="rounded-2xl border border-edge bg-surface p-4">
            <div className="flex items-baseline gap-2">
              <div className="font-semibold text-sm flex-1 min-w-0">{n.t}</div>
              <span className={`text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 shrink-0 ${n.tagCls}`}>{n.tag}</span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed mt-1.5">{n.b}</p>
            {n.cta && <Link to={n.cta.to} className="inline-block mt-2 text-xs text-vblue hover:underline">{n.cta.label} →</Link>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Insights() {
  const [items, setItems] = useState(null);
  const [month, setMonth] = useState('');
  const [months, setMonths] = useState([]);
  const [visits, setVisits] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const cur = await api('/budget/months/current/');
        setMonth(cur.month);
        const b = await api(`/budget/months/ensure/?month=${cur.month}`);
        setItems(b.items || []);
        // Confirmed visit lines feed the most-visited card and the per-place counts.
        api('/budget/place-visits/?budget=current')
          .then((vs) => setVisits((Array.isArray(vs) ? vs : []).filter((v) => v.confirmed)))
          .catch(() => { /* counts are best-effort */ });
        try {
          const all = await api('/budget/months/');
          const list = (Array.isArray(all) ? all : (all.results || [])).slice()
            .sort((x, y) => String(x.month).localeCompare(String(y.month))).slice(-8);
          setMonths(list);
        } catch { /* trend is best-effort */ }
      } catch { setItems([]); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-ink text-slate-100">
      <AppNav active="insights" />
      <div className="max-w-5xl mx-auto px-5 py-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-baseline gap-3">
            <h1 className="text-xl font-bold">📊 Spending insights</h1>
            {month && <span className="text-xs text-slate-500">{month}</span>}
          </div>
          <Link to="/app" className="text-sm text-slate-400 hover:text-white">← Dashboard</Link>
        </div>
        {items && items.length > 0 && <SummaryStrip items={items} visits={visits} months={months} month={month} />}
        <MonthTrend months={months} />
        {items && items.length > 0 && <Noticed items={items} months={months} month={month} />}
        {items === null
          ? <p className="text-slate-500 text-sm">Loading…</p>
          : items.length
            ? <SpendCharts items={items} visits={visits} />
            : <p className="text-slate-500 text-sm">No spending logged this month yet.</p>}
      </div>
    </div>
  );
}
